import { useEffect, useRef, useState } from "react";
import profileImg from "../assets/img/profile-banner.png";
import firstupImg from "../assets/img/firstup-engagement.png";
import gosearchImg from "../assets/img/gosearch-search.png";
import appleImg from "../assets/img/apple-store.png";
import aboutFieldIntro from "../assets/img/about-field-intro.png";
import aboutFieldExp from "../assets/img/about-field-exp.png";
import csmBadge from "../assets/img/badge-7227.png";
import cspoBadge from "../assets/img/cspo-badge.png";
import toolClaude from "../assets/img/tools/claude.svg";
import toolConfluence from "../assets/img/tools/confluence.svg";
import toolCursor from "../assets/img/tools/cursor.svg";
import toolDatabricks from "../assets/img/tools/databricks.svg";
import toolDbt from "../assets/img/tools/dbt.svg";
import toolFigma from "../assets/img/tools/figma.svg";
import toolGemini from "../assets/img/tools/googlegemini.svg";
import toolGithub from "../assets/img/tools/github.svg";
import toolHuggingface from "../assets/img/tools/huggingface.svg";
import toolJira from "../assets/img/tools/jira.svg";
import toolLangchain from "../assets/img/tools/langchain.svg";
import toolLinear from "../assets/img/tools/linear.svg";
import toolLooker from "../assets/img/tools/looker.svg";
import toolMiro from "../assets/img/tools/miro.svg";
import toolMixpanel from "../assets/img/tools/mixpanel.svg";
import toolNotion from "../assets/img/tools/notion.svg";
import toolOpenai from "../assets/img/tools/openai.svg";
import toolPosthog from "../assets/img/tools/posthog.svg";
import toolPython from "../assets/img/tools/python.svg";
import toolSalesforce from "../assets/img/tools/salesforce.svg";
import toolSlack from "../assets/img/tools/slack.svg";
import toolSnowflake from "../assets/img/tools/snowflake.svg";
import toolTableau from "../assets/img/tools/tableau.svg";
import toolZapier from "../assets/img/tools/zapier.svg";
import { AboutSky } from "./AboutSky";
import { GithubChart } from "./GithubChart";

// `dark` marks near-black marks that need lifting on the night theme.
const tools = [
  { name: "ChatGPT", src: toolOpenai, dark: true },
  { name: "Claude", src: toolClaude },
  { name: "Gemini", src: toolGemini },
  { name: "Cursor", src: toolCursor, dark: true },
  { name: "LangChain", src: toolLangchain },
  { name: "Hugging Face", src: toolHuggingface },
  { name: "Python", src: toolPython },
  { name: "SQL" },
  { name: "Snowflake", src: toolSnowflake },
  { name: "dbt", src: toolDbt },
  { name: "Databricks", src: toolDatabricks },
  { name: "PostHog", src: toolPosthog, dark: true },
  { name: "Mixpanel", src: toolMixpanel },
  { name: "Looker", src: toolLooker },
  { name: "Tableau", src: toolTableau },
  { name: "Jira", src: toolJira },
  { name: "Confluence", src: toolConfluence, dark: true },
  { name: "Linear", src: toolLinear },
  { name: "Notion", src: toolNotion, dark: true },
  { name: "Slack", src: toolSlack, dark: true },
  { name: "Figma", src: toolFigma },
  { name: "Miro", src: toolMiro, dark: true },
  { name: "Salesforce", src: toolSalesforce },
  { name: "GitHub", src: toolGithub, dark: true },
  { name: "Zapier", src: toolZapier },
];

const polaroids = [
  { id: "p1", src: firstupImg, alt: "Firstup engagement metrics for compliance and training initiatives" },
  { id: "p2", src: gosearchImg, alt: "GoSearch unified results across Notion, Drive, and Q&A" },
  { id: "p3", src: appleImg, alt: "Apple online store" },
  { id: "p4", src: profileImg, alt: "Sukrit Phaopanich" },
];

const roles = [
  {
    company: "GoLinks",
    title: "Technical Product Manager, AI",
    dates: "Jun 2025–Present",
    summary:
      "Sole PM for GoSearch AI, owning strategy, roadmap, customer discovery, monetization, and launch across enterprise search, retrieval, evaluation, and agentic workflows.",
    points: [
      "Rebuilt and shipped GoSearch 4.0 for iPhone, iPad, and Android on 14 October 2026, with no engineering team.",
      "Led the integrations catalog: 100+ apps across indexed, federated, and MCP connectors, 33 of them with MCP.",
      "Led the GoSearch MCP partnership with Claude, listed in the Claude app directory on 10 September 2026.",
      "Led GoSearch AI Answers from concept to launch across Drive, Confluence, Slack, Jira, and Salesforce, cutting average information-finding time by 60%+.",
      "Built LLM evaluation and feedback loops that improved answer quality 25% and, through A/B tests on retrieval and reranking, lifted response accuracy 30%.",
      "Shipped agentic read/write workflows (function calling + MCP) and a 0-to-1 freemium motion that contributed $1M+ in incremental revenue.",
    ],
  },
  {
    company: "Firstup",
    title: "Product Manager, Data",
    dates: "Mar 2022–May 2025",
    summary:
      "Owned data-platform products spanning APIs, analytics pipelines, and dashboards for Fortune 100 customers, with measurable cost and onboarding impact.",
    points: [
      "Onboarded 250+ U.S. and E.U. clients onto a new data platform and cut integration lead time by 70%, including sensitive geospatial compliance.",
      "Launched two predictive analytics applications from 0-to-1 with no dedicated engineering team, reducing operating costs by $1M+ and labor by 90% across 15+ Fortune 100s.",
      "Led a major platform feature that increased user engagement 30% in its first month, with a 98% strategic roadmap hit rate.",
    ],
  },
  {
    company: "Apple",
    title: "Application Engineer",
    dates: "May 2021–Mar 2022",
    summary: "Shipped Apple Online Store features and a real-time monitoring platform used at commercial scale, via Infosys.",
    points: [
      "Shipped storefront features that increased engagement 15% and contributed to a 20% lift in online sales.",
      "Built a data monitoring platform tracking 100k+ campaigns, cutting engineering support time 70% and support tickets 10%.",
    ],
  },
];

export const About = () => {
  const [open, setOpen] = useState(0);
  const [moved, setMoved] = useState(false);
  const offsets = useRef({});
  const stageRef = useRef(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    let active = null;

    const coarse = window.matchMedia("(hover: none) and (pointer: coarse)").matches;

    const onDown = (e) => {
      const tile = e.target.closest(".ai-polaroid");
      if (!tile) return;
      const id = tile.dataset.id;
      const start = offsets.current[id] || { x: 0, y: 0 };
      active = {
        id,
        tile,
        originX: e.clientX - start.x,
        originY: e.clientY - start.y,
        sx: e.clientX,
        sy: e.clientY,
        pointerId: e.pointerId,
        locked: !coarse,
      };
      if (!coarse) {
        tile.classList.add("is-dragging");
        tile.setPointerCapture(e.pointerId);
      }
    };

    const onMove = (e) => {
      if (!active || e.pointerId !== active.pointerId) return;
      if (!active.locked) {
        const dx = e.clientX - active.sx;
        const dy = e.clientY - active.sy;
        if (Math.hypot(dx, dy) < 8) return;
        if (Math.abs(dy) >= Math.abs(dx)) {
          active = null;
          return;
        }
        active.locked = true;
        active.tile.classList.add("is-dragging");
        try { active.tile.setPointerCapture(e.pointerId); } catch { /* already released */ }
      }
      const x = e.clientX - active.originX;
      const y = e.clientY - active.originY;
      offsets.current[active.id] = { x, y };
      active.tile.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      setMoved(true);
    };

    const onUp = (e) => {
      if (!active || (e && e.pointerId !== active.pointerId)) return;
      active.tile.classList.remove("is-dragging");
      if (active.locked) {
        active.tile.classList.add("is-dropping");
        const tile = active.tile;
        window.setTimeout(() => tile.classList.remove("is-dropping"), 500);
      }
      active = null;
    };

    stage.addEventListener("pointerdown", onDown);
    stage.addEventListener("pointermove", onMove);
    stage.addEventListener("pointerup", onUp);
    stage.addEventListener("pointercancel", onUp);
    return () => {
      stage.removeEventListener("pointerdown", onDown);
      stage.removeEventListener("pointermove", onMove);
      stage.removeEventListener("pointerup", onUp);
      stage.removeEventListener("pointercancel", onUp);
    };
  }, []);

  const reset = () => {
    offsets.current = {};
    stageRef.current?.querySelectorAll(".ai-polaroid").forEach((tile) => {
      tile.classList.add("is-settling");
      tile.style.transform = "translate3d(0,0,0)";
      window.setTimeout(() => tile.classList.remove("is-settling"), 720);
    });
    setMoved(false);
  };

  return (
    <div className="about-view">
      <section className="about-intro" id="about">
        <div
          className="about-field about-field--intro"
          style={{ backgroundImage: `url(${aboutFieldIntro})` }}
          aria-hidden="true"
        />
        <div className="about-field-veil" aria-hidden="true" />
        <AboutSky tone="intro" />
        <div className="stack-lid" aria-hidden="true" />
        <div className="about-intro-stage">
          <div className="ai-scene" id="ai-scene">
            <div className="ai-content">
              <div className="ai-copy">
                <h2 className="ai-heading">
                  I ship products people can measure, not just decks they can admire.
                </h2>
                <div className="ai-body">
                  <p>
                    I run 0-to-1 as a full-stack PM. I set the bet, hold the bar, and when a
                    layer has no team, <strong>I build it</strong>, then I put a number on it.
                  </p>
                  <p>
                    GoSearch, including the iOS and Android apps I shipped alone. Before that,
                    Fortune 100 rollouts at Firstup. The unglamorous path is the job.
                  </p>
                </div>
              </div>
              <div className="ai-photos-frame">
                <div className={`ai-photos ${moved ? "has-moved" : ""}`} ref={stageRef}>
                  {polaroids.map((item) => (
                    <figure className={`ai-polaroid ${item.id}`} data-id={item.id} key={item.id}>
                      <span className="ai-card">
                        <span className="ai-photo">
                          <img src={item.src} alt={item.alt} />
                        </span>
                      </span>
                    </figure>
                  ))}
                  <button type="button" className="ai-photos-reset" aria-label="Reset photo positions" onClick={reset}>
                    ↺
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-exp">
        <div
          className="about-field about-field--exp"
          style={{ backgroundImage: `url(${aboutFieldExp})` }}
          aria-hidden="true"
        />
        <AboutSky tone="exp" />
        <div className="stack-lid" aria-hidden="true" />
        <div className="aexp-roles">
          <p className="aexp-label">Professional Experience</p>
          <div className="aexp-list">
          {roles.map((role, index) => {
            const isOpen = open === index;
            return (
              <div className={`aexp-item ${isOpen ? "is-open" : ""}`} key={role.company}>
                <button
                  className="aexp-head"
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? -1 : index)}
                >
                  <span className="aexp-company">
                    <span className="aexp-name">{role.company}</span>
                    <span className="aexp-role">{role.title}</span>
                  </span>
                  <span className="aexp-meta">
                    <span className="aexp-date">{role.dates}</span>
                    <span className="aexp-toggle" aria-hidden="true" />
                  </span>
                </button>
                <div className="aexp-panel">
                  <div className="aexp-panel-inner">
                    <p>{role.summary}</p>
                    <ul className="aexp-bullets">
                      {role.points.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </div>

        <div className="aexp-creds">
          <p className="aexp-block-kicker">Scrum Alliance</p>
          <div className="aexp-certs" aria-label="Scrum Alliance certifications">
            <figure className="pgf-cert">
              <img src={csmBadge} alt="Certified ScrumMaster badge" />
              <figcaption>
                <strong>CSM</strong>
                <span>Certified ScrumMaster</span>
              </figcaption>
            </figure>
            <figure className="pgf-cert">
              <img src={cspoBadge} alt="Certified Scrum Product Owner badge" />
              <figcaption>
                <strong>CSPO</strong>
                <span>Certified Scrum Product Owner</span>
              </figcaption>
            </figure>
          </div>
        </div>

        <div className="aexp-tools">
          <p className="aexp-block-kicker">In the toolkit</p>
          <h3 className="aexp-tools-title">Tools I actually use</h3>
          <div className="logo-marquee-wrap tool-marquee" aria-label="Tools">
            <div className="logo-marquee-inner">
              {[...tools, ...tools].map((tool, i) => (
                <span className={`tool-mark${tool.dark ? " is-dark" : ""}`} key={`${tool.name}-${i}`}>
                  {tool.src ? <img src={tool.src} alt="" /> : null}
                  <span>{tool.name}</span>
                </span>
              ))}
            </div>
          </div>
        </div>

        <GithubChart />
      </section>
    </div>
  );
};
